import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { StudentProfile, Booking, StudentTripStats } from '../../types';
import { useRouter } from '../../router/Router';
import { useAuth } from '../../auth/AuthContext';
import { authService } from '../../auth/authService';
import { adminBookingService } from '../../services/adminBookingService';
import {
  User,
  Users,
  Search,
  Eye,
  ShieldAlert,
  ShieldCheck,
  MapPin,
  GraduationCap,
  RefreshCw,
  CheckCircle2,
  X,
  Trash2,
  KeyRound,
  Copy,
  Plus,
  Phone,
  Mail,
  Sparkles,
  Bus,
  Edit,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Download,
  AlertTriangle,
  Ticket,
  Calendar,
  Clock,
} from 'lucide-react';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import { universityService } from '../../services/universityService';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

const WhatsAppIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={{ flexShrink: 0 }}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.456 5.711 1.457h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const AdminStudents: React.FC = () => {
  const { navigate } = useRouter();
  const { isOperator } = useAuth();

  const formatCreationDateTime = (dateVal?: any) => {
    if (!dateVal) return { date: '—', time: '' };
    try {
      let d: Date;
      if (typeof dateVal === 'string') {
        d = new Date(dateVal);
      } else if (dateVal?.toDate && typeof dateVal.toDate === 'function') {
        d = dateVal.toDate();
      } else if (dateVal?.seconds) {
        d = new Date(dateVal.seconds * 1000);
      } else {
        d = new Date(dateVal);
      }
      if (isNaN(d.getTime())) return { date: '—', time: '' };

      const dateStr = d.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const timeStr = d.toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: '—', time: '' };
    }
  };

  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [dbUnis, setDbUnis] = useState<{ id: string; name: string; governorate: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [uniFilter, setUniFilter] = useState<string>('ALL');
  const [routeFilter, setRouteFilter] = useState<string>('ALL');
  const [tripFilter, setTripFilter] = useState<string>('ALL');
  const [showAllUniversities, setShowAllUniversities] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(15);
  const [selectedStudent, setSelectedStudent] = useState<StudentProfile | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  /**
   * Opens student's WhatsApp in new tab and prepares the official OTO Students welcome message
   */
  const handleSendWhatsAppWelcome = (student: StudentProfile) => {
    if (!student.phoneNumber || !student.phoneNumber.trim()) {
      setMessage({
        type: 'error',
        text: `لا يوجد رقم هاتف مسجل للطالب "${student.fullName}" لفتح محادثة الواتساب.`,
      });
      return;
    }
    const url = adminBookingService.getStudentWelcomeWhatsAppUrl(student);
    window.open(url, '_blank', 'noopener,noreferrer');
    setMessage({
      type: 'success',
      text: `تم فتح محادثة الواتساب وتجهيز الرسالة الترحيبية للطالب "${student.fullName}".`,
    });
  };

  // Load Active Universities from Database
  useEffect(() => {
    const fetchUnis = async () => {
      try {
        const list = await universityService.getActiveUniversities();
        if (list && list.length > 0) {
          setDbUnis(list.map((u) => ({ id: u.id, name: u.name, governorate: u.governorate || 'مصر' })));
        }
      } catch (err) {
        console.warn('Could not fetch active universities:', err);
      }
    };
    fetchUnis();
  }, []);

  // Compute all distinct registered universities (Seed + Database + Registered Students)
  const allUniversityOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; governorate: string }>();

    // 1. Seed universities
    SEED_UNIVERSITIES.forEach((u) => {
      if (u.name) {
        map.set(u.name.trim(), { id: u.id, name: u.name.trim(), governorate: u.governorate || 'مصر' });
      }
    });

    // 2. Database universities
    dbUnis.forEach((u) => {
      if (u.name) {
        map.set(u.name.trim(), { id: u.id, name: u.name.trim(), governorate: u.governorate || 'مصر' });
      }
    });

    // 3. Registered students' universities
    students.forEach((s) => {
      const raw = s.universityName?.trim();
      if (raw && raw !== 'أخرى' && raw !== 'جامعة أخرى' && raw !== 'غير محدد' && !map.has(raw)) {
        map.set(raw, {
          id: s.universityId || `uni-${raw.replace(/\s+/g, '-').toLowerCase()}`,
          name: raw,
          governorate: s.universityGovernorate || 'مصر',
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [dbUnis, students]);

  // Change Password Modal State
  const [passwordModalStudent, setPasswordModalStudent] = useState<StudentProfile | null>(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [copiedPass, setCopiedPass] = useState(false);

  // Edit Student Modal State
  const [editingStudent, setEditingStudent] = useState<StudentProfile | null>(null);
  const [editForm, setEditForm] = useState<{
    fullName: string;
    phoneNumber: string;
    email: string;
    universityId: string;
    universityName: string;
    universityGovernorate: string;
    isCustomUniversity: boolean;
    college: string;
    academicYear: string;
    assignedRoute: string;
    address: string;
    defaultPickupStation: string;
    status: 'ACTIVE' | 'SUSPENDED';
    notes: string;
  }>({
    fullName: '',
    phoneNumber: '',
    email: '',
    universityId: '',
    universityName: '',
    universityGovernorate: '',
    isCustomUniversity: false,
    college: '',
    academicYear: '',
    assignedRoute: '',
    address: '',
    defaultPickupStation: '',
    status: 'ACTIVE',
    notes: '',
  });

  const handleOpenEditStudent = (student: StudentProfile) => {
    setEditingStudent(student);

    const sUniName = (student.universityName || '').trim();
    const sUniId = (student.universityId || '').trim();

    // Check if student's university matches an existing option
    const matched = allUniversityOptions.find(
      (u) =>
        (sUniId && u.id === sUniId) ||
        (sUniName && u.name.trim().toLowerCase() === sUniName.toLowerCase())
    );

    const isCustom = !matched && !!sUniName;

    setEditForm({
      fullName: student.fullName || '',
      phoneNumber: student.phoneNumber || '',
      email: student.email || '',
      universityId: matched ? matched.id : (isCustom ? 'OTHER' : ''),
      universityName: sUniName || (matched ? matched.name : ''),
      universityGovernorate: student.universityGovernorate || (matched ? matched.governorate : 'مصر'),
      isCustomUniversity: isCustom,
      college: student.college || '',
      academicYear: student.academicYear || '',
      assignedRoute: student.assignedRoute || '',
      address: student.address || '',
      defaultPickupStation: student.defaultPickupStation || '',
      status: (student.status as 'ACTIVE' | 'SUSPENDED') || 'ACTIVE',
      notes: (student as any).notes || '',
    });
  };

  const handleSaveStudentEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStudent) return;

    if (!editForm.fullName.trim()) {
      alert('يرجى إدخال اسم الطالب بالكامل.');
      return;
    }

    const cleanPhone = editForm.phoneNumber.replace(/[^0-9]/g, '');
    if (!cleanPhone) {
      alert('رقم الهاتف إجباري ولا يمكن تركه فارغاً.');
      return;
    }

    if (!/^01[0125][0-9]{8}$/.test(cleanPhone)) {
      alert('يرجى إدخال رقم هاتف محمول مصري صحيح مكون من 11 رقماً يبدأ بـ 010 أو 011 أو 012 أو 015.');
      return;
    }

    if (editForm.isCustomUniversity && !editForm.universityName.trim()) {
      alert('يرجى كتابة اسم الجامعة يدوياً أو اختيار جامعة من القائمة المسجلة.');
      return;
    }

    const finalUniName = editForm.isCustomUniversity
      ? editForm.universityName.trim()
      : (allUniversityOptions.find((u) => u.id === editForm.universityId)?.name || editForm.universityName.trim() || 'جامعة غير محددة');

    const finalUniId = editForm.isCustomUniversity
      ? `custom-${finalUniName.replace(/\s+/g, '-').toLowerCase()}`
      : editForm.universityId;

    const finalGov = editForm.universityGovernorate?.trim() || 'مصر';

    setActionLoading(true);
    try {
      // 1. Sync Route with student document, active bookings, and demand records
      await adminBookingService.syncStudentRouteToBookingsAndDemand(
        editingStudent.uid,
        editForm.assignedRoute,
        editForm.defaultPickupStation
      );

      const updatedFields = {
        fullName: editForm.fullName.trim(),
        phoneNumber: editForm.phoneNumber.trim(),
        email: editForm.email.trim(),
        universityId: finalUniId,
        universityName: finalUniName,
        universityGovernorate: finalGov,
        college: editForm.college.trim(),
        academicYear: editForm.academicYear.trim(),
        assignedRoute: editForm.assignedRoute.trim(),
        address: editForm.address.trim(),
        defaultPickupStation: editForm.defaultPickupStation.trim(),
        status: editForm.status,
        notes: editForm.notes.trim(),
        updatedAt: new Date().toISOString(),
      };

      try {
        const docRef = doc(db, 'students', editingStudent.uid);
        await updateDoc(docRef, updatedFields);
      } catch (fsErr) {
        console.warn('Direct Firestore updateDoc failed, updated locally:', fsErr);
      }

      setStudents((prev) =>
        prev.map((s) => (s.uid === editingStudent.uid ? { ...s, ...updatedFields } : s))
      );

      // Refresh bookings so updated assignedRoute is immediately reflected
      adminBookingService.getAllBookings(true).then(setBookings).catch(() => {});

      if (selectedStudent?.uid === editingStudent.uid) {
        setSelectedStudent((prev) => (prev ? { ...prev, ...updatedFields } : null));
      }

      setMessage({
        text: `تم تحديث بيانات الطالب "${editForm.fullName}" ومزامنة خط السير مع كافة حجوزاته وطلباته بنجاح!`,
        type: 'success',
      });
      setEditingStudent(null);
    } catch (err: any) {
      alert(`حدث خطأ أثناء حفظ التعديلات: ${err?.message}`);
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Fetch students & bookings with instant in-memory cache
  const loadStudents = async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const [list, bookingsList] = await Promise.all([
        adminBookingService.getAllStudents(forceRefresh),
        adminBookingService.getAllBookings(forceRefresh),
      ]);
      setStudents(list);
      setBookings(bookingsList);
    } catch (err) {
      console.warn('Falling back to empty students list:', err);
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  // Helper: Standardize / group university name variations
  const getStandardUniversityName = (rawName?: string) => {
    if (!rawName || !rawName.trim()) return 'جامعة غير محددة';
    const norm = rawName.trim().replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/\s+/g, ' ');
    if (norm.includes('سادات') && norm.includes('اهليه')) {
      return 'جامعة السادات الأهلية';
    }
    if (norm.includes('رياده') || norm.includes('الريادة')) {
      return 'جامعة الريادة';
    }
    if (norm.includes('منوفيه') && norm.includes('اهليه')) {
      return 'جامعة المنوفية الأهلية';
    }
    if (norm.includes('اسكندريه') && norm.includes('اهليه')) {
      return 'جامعة الإسكندرية الأهلية';
    }
    if (norm.includes('مطروح')) {
      return 'جامعة مطروح';
    }
    if (norm.includes('دمنهور')) {
      return 'جامعة دمنهور';
    }
    if (norm === 'جامعه اخري' || norm === 'جامعة اخرى' || norm === 'اخري') {
      return 'جامعة أخرى';
    }
    return rawName.trim();
  };

  // University Student Counts Breakdown (Standardized and Grouped)
  const universityStats = useMemo(() => {
    const map: Record<string, { count: number; gov: string; rawNames: Set<string> }> = {};
    for (const s of students) {
      const stdName = getStandardUniversityName(s.universityName || s.universityId);
      if (!map[stdName]) {
        let defaultGov = s.universityGovernorate || '';
        if (!defaultGov) {
          if (stdName.includes('السادات') || stdName.includes('الريادة') || stdName.includes('المنوفية')) defaultGov = 'المنوفية';
          else if (stdName.includes('الإسكندرية') || stdName.includes('اسكندرية')) defaultGov = 'الإسكندرية';
          else if (stdName.includes('مطروح')) defaultGov = 'مطروح';
          else if (stdName.includes('دمنهور')) defaultGov = 'البحيرة';
          else defaultGov = 'مصر';
        }
        map[stdName] = { count: 0, gov: defaultGov, rawNames: new Set() };
      }
      map[stdName].count += 1;
      if (s.universityName) map[stdName].rawNames.add(s.universityName);
    }
    return Object.entries(map).sort((a, b) => b[1].count - a[1].count);
  }, [students]);

  // Unique Routes Breakdown
  const routeStats = useMemo(() => {
    const set = new Set<string>();
    for (const s of students) {
      if (s.assignedRoute && s.assignedRoute.trim()) {
        set.add(s.assignedRoute.trim());
      }
    }
    return Array.from(set).sort();
  }, [students]);

  // Dynamic Student Trip Stats Map (عدد الرحلات المحجوزة والمتبقية لكل طالب)
  const studentTripStatsMap = useMemo(() => {
    const map = new Map<string, StudentTripStats>();
    const today = new Date().toISOString().split('T')[0];

    // Group bookings by student
    const studentBookingsMap = new Map<string, Booking[]>();
    for (const b of bookings) {
      const keys = [b.studentId, b.studentSnapshot?.email, b.studentSnapshot?.phoneNumber].filter(Boolean) as string[];
      for (const k of keys) {
        if (!studentBookingsMap.has(k)) {
          studentBookingsMap.set(k, []);
        }
        studentBookingsMap.get(k)!.push(b);
      }
    }

    for (const s of students) {
      const sBookings =
        studentBookingsMap.get(s.uid) ||
        (s.email ? studentBookingsMap.get(s.email) : undefined) ||
        (s.phoneNumber ? studentBookingsMap.get(s.phoneNumber) : undefined) ||
        [];

      const uniqueBookings = Array.from(new Map(sBookings.map((b) => [b.id, b])).values());

      const valid = uniqueBookings.filter(
        (b) =>
          b.status !== 'CANCELLED' &&
          b.status !== 'DELETED' &&
          (b.bookingStatus as string) !== 'CANCELLED' &&
          (b.bookingStatus as string) !== 'DELETED' &&
          b.paymentStatus !== 'REJECTED' &&
          b.paymentStatus !== 'CANCELLED'
      );

      const totalBooked = valid.length;
      const remaining = valid.filter(
        (b) => b.tripDate >= today && b.status !== 'COMPLETED' && (b.bookingStatus as string) !== 'COMPLETED'
      ).length;
      const completed = valid.filter(
        (b) => b.tripDate < today || b.status === 'COMPLETED' || (b.bookingStatus as string) === 'COMPLETED'
      ).length;

      map.set(s.uid, {
        totalBooked,
        remaining,
        completed,
        isMultiTripSubscriber: totalBooked > 1,
        activeBookingCodes: valid.map((b) => b.bookingCode),
      });
    }

    return map;
  }, [students, bookings]);

  // Filtered list
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchesSearch =
        searchTerm === '' ||
        s.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.phoneNumber?.includes(searchTerm) ||
        s.universityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.assignedRoute?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.college?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' || s.status === statusFilter;

      const stdUni = getStandardUniversityName(s.universityName || s.universityId);
      const matchesUni =
        uniFilter === 'ALL' ||
        s.universityName === uniFilter ||
        stdUni === uniFilter;

      const matchesRoute =
        routeFilter === 'ALL' ||
        (routeFilter === 'UNASSIGNED' && (!s.assignedRoute || !s.assignedRoute.trim())) ||
        (routeFilter === 'ASSIGNED' && !!s.assignedRoute && !!s.assignedRoute.trim()) ||
        s.assignedRoute === routeFilter;

      const tStats = studentTripStatsMap.get(s.uid) || {
        totalBooked: 0,
        remaining: 0,
        completed: 0,
        isMultiTripSubscriber: false,
      };

      const matchesTrip =
        tripFilter === 'ALL' ||
        (tripFilter === 'MULTI' && tStats.isMultiTripSubscriber) ||
        (tripFilter === 'REMAINING' && tStats.remaining > 0) ||
        (tripFilter === 'COMPLETED' && tStats.totalBooked > 0 && tStats.remaining === 0) ||
        (tripFilter === 'NO_TRIPS' && tStats.totalBooked === 0);

      return matchesSearch && matchesStatus && matchesUni && matchesRoute && matchesTrip;
    });
  }, [students, searchTerm, statusFilter, uniFilter, routeFilter, tripFilter, studentTripStatsMap]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, statusFilter, uniFilter, routeFilter, tripFilter]);

  // Overall KPIs
  const stats = useMemo(() => {
    const total = students.length;
    const active = students.filter((s) => s.status === 'ACTIVE').length;
    const suspended = students.filter((s) => s.status === 'SUSPENDED').length;
    const withoutRoute = students.filter((s) => !s.assignedRoute || !s.assignedRoute.trim()).length;
    const multiTripCount = Array.from(studentTripStatsMap.values()).filter((st: StudentTripStats) => st.isMultiTripSubscriber).length;
    const withRemainingCount = Array.from(studentTripStatsMap.values()).filter((st: StudentTripStats) => st.remaining > 0).length;
    return { total, active, suspended, withoutRoute, multiTripCount, withRemainingCount };
  }, [students, studentTripStatsMap]);

  // Sliced students based on visible count (15 initially + 10 more on button click)
  const paginatedStudents = useMemo(() => {
    return filteredStudents.slice(0, visibleCount);
  }, [filteredStudents, visibleCount]);

  // Reset All Filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setUniFilter('ALL');
    setRouteFilter('ALL');
    setTripFilter('ALL');
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredStudents.length === 0) {
      alert('لا توجد بيانات طلاب لتصديرها');
      return;
    }
    const headers = [
      'المعرف UID',
      'الاسم بالكامل',
      'رقم الهاتف',
      'البريد الإلكتروني',
      'الجامعة',
      'الكلية',
      'المحافظة',
      'خط السير',
      'محطة الركوب',
      'الرحلات المحجوزة',
      'الرحلات المتبقية',
      'نوع الاشتراك',
      'العنوان',
      'الحالة',
      'تاريخ ووقت إنشاء الحساب',
    ];
    const rows = filteredStudents.map((s) => {
      const tStats = studentTripStatsMap.get(s.uid);
      return [
        `"${s.uid}"`,
        `"${s.fullName || ''}"`,
        `"${s.phoneNumber || ''}"`,
        `"${s.email || ''}"`,
        `"${s.universityName || s.universityId || ''}"`,
        `"${s.college || ''}"`,
        `"${s.universityGovernorate || ''}"`,
        `"${s.assignedRoute || ''}"`,
        `"${s.defaultPickupStation || ''}"`,
        `"${tStats?.totalBooked || 0}"`,
        `"${tStats?.remaining || 0}"`,
        `"${tStats?.isMultiTripSubscriber ? 'مشترك في عدة رحلات' : (tStats?.totalBooked ? 'حجز فردي' : 'بدون رحلات')}"`,
        `"${s.address || ''}"`,
        `"${s.status === 'ACTIVE' ? 'نشط' : 'معلق'}"`,
        `"${s.createdAt ? new Date(s.createdAt).toLocaleString('ar-EG') : ''}"`,
      ];
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `oto_students_list_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle Suspend / Reactivate
  const handleToggleStatus = async (student: StudentProfile) => {
    const nextStatus = student.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    const actionLabel = nextStatus === 'SUSPENDED' ? 'تعليق حساب' : 'إعادة تفعيل حساب';

    if (!window.confirm(`هل أنت متأكد من ${actionLabel} الطالب "${student.fullName}"؟`)) {
      return;
    }

    setActionLoading(true);
    try {
      const docRef = doc(db, 'students', student.uid);
      await updateDoc(docRef, {
        status: nextStatus,
        updatedAt: new Date().toISOString(),
      });

      setStudents((prev) =>
        prev.map((s) => (s.uid === student.uid ? { ...s, status: nextStatus } : s))
      );

      if (selectedStudent?.uid === student.uid) {
        setSelectedStudent((prev) => (prev ? { ...prev, status: nextStatus } : null));
      }

      setMessage({
        text: `تم ${nextStatus === 'SUSPENDED' ? 'تعليق' : 'تفعيل'} حساب الطالب بنجاح.`,
        type: 'success',
      });
    } catch (err: any) {
      setStudents((prev) =>
        prev.map((s) => (s.uid === student.uid ? { ...s, status: nextStatus } : s))
      );
      setMessage({
        text: `تم تحديث حالة الطالب إلى ${nextStatus === 'SUSPENDED' ? 'معلق' : 'نشط'} محلياً.`,
        type: 'success',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Delete Student
  const handleDeleteStudent = async (student: StudentProfile) => {
    if (isOperator) {
      setMessage({
        text: 'عذرًا، حساب التشغيل لا يمتلك صلاحية حذف حسابات الطلاب نهائياً. يرجى مراجعة الإدارة العليا.',
        type: 'error',
      });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    if (!window.confirm(`هل أنت متأكد من حذف حساب الطالب "${student.fullName}" نهائياً من قاعدة البيانات؟ سيتم مسح كافة سجلاته.`)) {
      return;
    }

    setActionLoading(true);
    try {
      const docRef = doc(db, 'students', student.uid);
      await deleteDoc(docRef);
      setStudents((prev) => prev.filter((s) => s.uid !== student.uid));
      if (selectedStudent?.uid === student.uid) {
        setSelectedStudent(null);
      }
      setMessage({
        text: `تم حذف حساب الطالب "${student.fullName}" نهائياً من قاعدة البيانات بنجاح!`,
        type: 'success',
      });
    } catch (err: any) {
      console.warn('Student delete local fallback:', err);
      setStudents((prev) => prev.filter((s) => s.uid !== student.uid));
      if (selectedStudent?.uid === student.uid) {
        setSelectedStudent(null);
      }
      setMessage({ text: `تم حذف حساب الطالب محلياً.`, type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Open Change Password Modal
  const handleOpenChangePassword = (student: StudentProfile) => {
    setPasswordModalStudent(student);
    const num = Math.floor(100000 + Math.random() * 900000);
    setNewPasswordValue(`Oto${num}!`);
    setCopiedPass(false);
  };

  // Save New Password
  const handleSaveNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordModalStudent || !newPasswordValue.trim()) return;

    setActionLoading(true);
    const nowIso = new Date().toISOString();

    try {
      const docRef = doc(db, 'students', passwordModalStudent.uid);
      await updateDoc(docRef, {
        password: newPasswordValue.trim(),
        updatedAt: nowIso,
      });

      setStudents((prev) =>
        prev.map((s) =>
          s.uid === passwordModalStudent.uid
            ? { ...s, password: newPasswordValue.trim(), updatedAt: nowIso }
            : s
        )
      );

      setMessage({
        text: `تم تغيير كلمة مرور الطالب "${passwordModalStudent.fullName}" بنجاح!`,
        type: 'success',
      });
      setPasswordModalStudent(null);
    } catch (err: any) {
      setStudents((prev) =>
        prev.map((s) =>
          s.uid === passwordModalStudent.uid
            ? { ...s, password: newPasswordValue.trim(), updatedAt: nowIso }
            : s
        )
      );
      setMessage({ text: 'تم تحديث كلمة المرور محلياً.', type: 'success' });
      setPasswordModalStudent(null);
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newPasswordValue);
    setCopiedPass(true);
    setTimeout(() => setCopiedPass(false), 3000);
  };

  const handleSendResetEmail = async () => {
    if (!passwordModalStudent?.email) return;
    setActionLoading(true);
    try {
      await authService.sendPasswordReset(passwordModalStudent.email);
      setMessage({
        text: `تم إرسال رابط إعادة تعيين كلمة المرور الرسمي إلى بريد الطالب (${passwordModalStudent.email}) بنجاح!`,
        type: 'success',
      });
    } catch (err: any) {
      alert('حدث خطأ أثناء إرسال الرابط: ' + (err?.message || 'يرجى المحاولة مجدداً'));
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <div className="flex items-center gap-2 mb-1">
            <span className="admin-badge-purple">شؤون الطلاب والمسارات</span>
            <span className="text-xs bg-slate-100 text-slate-700 font-bold px-2 py-0.5 rounded-full border">
              سجل الطلاب الشامل
            </span>
          </div>
          <h2>
            <Users size={24} className="text-[#E5232A]" />
            <span>سجل الطلاب المسجلين بالمنظومة</span>
          </h2>
          <p>
            إدارة حسابات الطلاب، أرقام الهواتف، البريد الإلكتروني، خطوط السير وتغيير كلمات المرور وحذف الحسابات.
          </p>
        </div>

        <div className="admin-header-actions-row">
          <button
            type="button"
            className="btn-admin-primary flex items-center gap-1.5"
            onClick={() => navigate('/admin/create-student')}
          >
            <Plus size={17} />
            <span>إنشاء حساب طالب جديد</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline flex items-center gap-1.5"
            onClick={handleExportCSV}
            title="تصدير قائمة الطلاب الحالية إلى ملف Excel / CSV"
          >
            <Download size={15} />
            <span>تصدير CSV</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline"
            onClick={() => loadStudents(true)}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Message alert */}
      {message && (
        <div
          className={`p-3 rounded-xl border mb-5 flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <CheckCircle2 size={18} />
          <span>{message.text}</span>
        </div>
      )}

      {/* Stats KPI Strip */}
      <div
        className="mb-5"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
        }}
      >
        <div
          className="admin-stat-card cursor-pointer hover:shadow-md transition-all"
          onClick={() => { setStatusFilter('ALL'); setRouteFilter('ALL'); }}
        >
          <div className="stat-icon blue">
            <Users size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.total}</span>
            <span className="stat-lbl">إجمالي الطلاب المسجلين</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:shadow-md transition-all"
          onClick={() => setStatusFilter('ACTIVE')}
        >
          <div className="stat-icon green">
            <ShieldCheck size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.active}</span>
            <span className="stat-lbl">حسابات نشطة (ACTIVE)</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:shadow-md transition-all"
          onClick={() => setStatusFilter('SUSPENDED')}
        >
          <div className="stat-icon red">
            <ShieldAlert size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.suspended}</span>
            <span className="stat-lbl">حسابات معلقة (SUSPENDED)</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:shadow-md transition-all"
          onClick={() => setRouteFilter('UNASSIGNED')}
        >
          <div className="stat-icon amber">
            <Bus size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.withoutRoute}</span>
            <span className="stat-lbl">بدون خط سير معين</span>
          </div>
        </div>

        <div
          className="admin-stat-card cursor-pointer hover:shadow-md transition-all"
          onClick={() => setUniFilter('ALL')}
        >
          <div className="stat-icon purple">
            <GraduationCap size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{universityStats.length}</span>
            <span className="stat-lbl">جامعات مقيد بها طلاب</span>
          </div>
        </div>
      </div>

      {/* University Student Counts Widget (توزيع الطلاب المنظم حسب الجامعات) */}
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden"
        style={{ padding: '18px 20px' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '14px',
            paddingBottom: '12px',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#FFF1F2',
                color: '#E5232A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GraduationCap size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                توزيع الطلاب حسب الجامعات المستهدفة
              </h3>
              <span style={{ fontSize: '11px', color: '#64748b' }}>
                {students.length} طالب موزعين على {universityStats.length} جامعة
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {uniFilter !== 'ALL' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#FFF1F2',
                  border: '1px solid #FECDD3',
                  color: '#E5232A',
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                }}
              >
                <span>تصفية نشطة: {uniFilter}</span>
                <button
                  type="button"
                  onClick={() => setUniFilter('ALL')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#E5232A',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title="إلغاء تصفية الجامعة"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {universityStats.length > 6 && (
              <button
                type="button"
                onClick={() => setShowAllUniversities(!showAllUniversities)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: showAllUniversities ? '#f1f5f9' : '#ffffff',
                  color: '#334155',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                <span>{showAllUniversities ? 'عرض أهم 6 جامعات فقط' : `عرض باقي الجامعات (${universityStats.length - 6}+)`}</span>
                {showAllUniversities ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </div>
        </div>

        {/* University Cards Grid (Explicit CSS Grid Layout) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
            gap: '12px',
          }}
        >
          {(showAllUniversities ? universityStats : universityStats.slice(0, 6)).map(([uniName, item]) => {
            const percent = Math.round((item.count / (students.length || 1)) * 100);
            const isFilterActive = uniFilter === uniName;

            return (
              <div
                key={uniName}
                onClick={() => setUniFilter(isFilterActive ? 'ALL' : uniName)}
                style={{
                  padding: '12px',
                  borderRadius: '12px',
                  border: isFilterActive ? '2px solid #E5232A' : '1px solid #e2e8f0',
                  backgroundColor: isFilterActive ? '#FFF1F2' : '#f8fafc',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  boxShadow: isFilterActive ? '0 2px 8px rgba(229, 35, 42, 0.15)' : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    gap: '6px',
                  }}
                >
                  <strong
                    style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: isFilterActive ? '#E5232A' : '#1e293b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '140px',
                    }}
                    title={uniName}
                  >
                    {uniName}
                  </strong>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 'bold',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      backgroundColor: isFilterActive ? '#E5232A' : '#ffffff',
                      color: isFilterActive ? '#ffffff' : '#E5232A',
                      border: '1px solid #FECDD3',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                    }}
                  >
                    {item.count} طالب
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <MapPin size={11} className="text-slate-400" />
                    <span>{item.gov || 'مصر'}</span>
                  </span>
                  <span style={{ fontWeight: 'bold', color: '#1e293b', fontFamily: 'monospace' }}>
                    {percent}%
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ width: '100%', backgroundColor: '#e2e8f0', height: '5px', borderRadius: '999px', overflow: 'hidden' }}>
                  <div
                    style={{
                      backgroundColor: '#E5232A',
                      height: '100%',
                      borderRadius: '999px',
                      width: `${percent}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Filter Tabs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '10px',
        }}
      >
        <button
          type="button"
          onClick={() => { setStatusFilter('ALL'); setRouteFilter('ALL'); }}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: statusFilter === 'ALL' && routeFilter === 'ALL' ? '1.5px solid #E5232A' : '1px solid #cbd5e1',
            backgroundColor: statusFilter === 'ALL' && routeFilter === 'ALL' ? '#FFF1F2' : '#ffffff',
            color: statusFilter === 'ALL' && routeFilter === 'ALL' ? '#E5232A' : '#475569',
            cursor: 'pointer',
          }}
        >
          جميع الطلاب ({students.length})
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('ACTIVE')}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: statusFilter === 'ACTIVE' ? '1.5px solid #16a34a' : '1px solid #cbd5e1',
            backgroundColor: statusFilter === 'ACTIVE' ? '#f0fdf4' : '#ffffff',
            color: statusFilter === 'ACTIVE' ? '#16a34a' : '#475569',
            cursor: 'pointer',
          }}
        >
          النشطين ({stats.active})
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('SUSPENDED')}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: statusFilter === 'SUSPENDED' ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
            backgroundColor: statusFilter === 'SUSPENDED' ? '#fef2f2' : '#ffffff',
            color: statusFilter === 'SUSPENDED' ? '#dc2626' : '#475569',
            cursor: 'pointer',
          }}
        >
          المعلقين ({stats.suspended})
        </button>

        <button
          type="button"
          onClick={() => setRouteFilter('UNASSIGNED')}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: routeFilter === 'UNASSIGNED' ? '1.5px solid #d97706' : '1px solid #cbd5e1',
            backgroundColor: routeFilter === 'UNASSIGNED' ? '#fffbeb' : '#ffffff',
            color: routeFilter === 'UNASSIGNED' ? '#d97706' : '#475569',
            cursor: 'pointer',
          }}
        >
          بدون خط سير ({stats.withoutRoute})
        </button>

        <button
          type="button"
          onClick={() => setTripFilter('MULTI')}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: tripFilter === 'MULTI' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
            backgroundColor: tripFilter === 'MULTI' ? '#eff6ff' : '#ffffff',
            color: tripFilter === 'MULTI' ? '#2563eb' : '#475569',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Ticket size={12} />
          <span>مشتركون في عدة رحلات ({stats.multiTripCount})</span>
        </button>

        <button
          type="button"
          onClick={() => setTripFilter('REMAINING')}
          style={{
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 'bold',
            border: tripFilter === 'REMAINING' ? '1.5px solid #16a34a' : '1px solid #cbd5e1',
            backgroundColor: tripFilter === 'REMAINING' ? '#f0fdf4' : '#ffffff',
            color: tripFilter === 'REMAINING' ? '#16a34a' : '#475569',
            cursor: 'pointer',
          }}
        >
          لديهم رحلات متبقية ({stats.withRemainingCount})
        </button>

        {(searchTerm || statusFilter !== 'ALL' || uniFilter !== 'ALL' || routeFilter !== 'ALL') && (
          <button
            type="button"
            onClick={handleResetFilters}
            style={{
              padding: '6px 12px',
              borderRadius: '999px',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '1px solid #cbd5e1',
              backgroundColor: '#f1f5f9',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginRight: 'auto',
            }}
          >
            <RotateCcw size={12} />
            <span>مسح جميع الفلاتر</span>
          </button>
        )}
      </div>

      {/* Toolbar & Filters */}
      <div className="admin-toolbar-card">
        <div className="admin-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="admin-search-input"
            placeholder="البحث باسم الطالب، رقم الهاتف، البريد، الجامعة، خط السير، الكلية، السكن..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
              }}
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className="admin-filter-group flex gap-2">
          {/* Status Filter */}
          <select
            className="admin-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">جميع الحالات ({students.length})</option>
            <option value="ACTIVE">نشط (ACTIVE)</option>
            <option value="SUSPENDED">معلق (SUSPENDED)</option>
          </select>

          {/* University Filter */}
          <select
            className="admin-filter-select"
            value={uniFilter}
            onChange={(e) => setUniFilter(e.target.value)}
          >
            <option value="ALL">كافة الجامعات ({universityStats.length})</option>
            {universityStats.map(([name, item]) => (
              <option key={name} value={name}>
                {name} ({item.count} طالب)
              </option>
            ))}
          </select>

          {/* Route Filter */}
          <select
            className="admin-filter-select"
            value={routeFilter}
            onChange={(e) => setRouteFilter(e.target.value)}
          >
            <option value="ALL">كافة خطوط السير</option>
            <option value="ASSIGNED">معين له خط سير</option>
            <option value="UNASSIGNED">بدون خط سير معين ({stats.withoutRoute})</option>
            {routeStats.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          {/* Trip Subscription Filter */}
          <select
            className="admin-filter-select"
            value={tripFilter}
            onChange={(e) => setTripFilter(e.target.value)}
          >
            <option value="ALL">كافة حالات الاشتراكات والرحلات</option>
            <option value="MULTI">مشتركون في عدة رحلات ({stats.multiTripCount})</option>
            <option value="REMAINING">لديهم رحلات متبقية قادمة ({stats.withRemainingCount})</option>
            <option value="COMPLETED">استهلكوا جميع رحلاتهم</option>
            <option value="NO_TRIPS">بدون حجوزات مسجلة</option>
          </select>

          {/* Results Badge */}
          <div
            style={{
              padding: '8px 12px',
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#334155',
              whiteSpace: 'nowrap',
            }}
          >
            عرض <span style={{ color: '#E5232A', fontFamily: 'monospace' }}>{filteredStudents.length}</span> طالب
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="admin-table-container">
        {isLoading ? (
          <div className="table-empty-state">
            <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
            <p>جاري تحميل قائمة الطلاب...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="table-empty-state">
            <User size={40} />
            <p>لا يوجد طلاب يطابقون شروط البحث الحالية.</p>
            {(searchTerm || statusFilter !== 'ALL' || uniFilter !== 'ALL' || routeFilter !== 'ALL') && (
              <button
                type="button"
                className="btn-admin-outline mt-3"
                onClick={handleResetFilters}
              >
                إعادة ضبط الفلاتر
              </button>
            )}
          </div>
        ) : (
          <table className="admin-data-table">
            <thead>
              <tr>
                <th style={{ minWidth: '220px' }}>اسم الطالب والتصنيف</th>
                <th style={{ minWidth: '150px' }}>تاريخ ووقت إنشاء الحساب</th>
                <th style={{ minWidth: '130px' }}>رقم الهاتف</th>
                <th style={{ minWidth: '180px' }}>البريد الإلكتروني</th>
                <th style={{ minWidth: '170px' }}>الجامعة والكلية</th>
                <th style={{ minWidth: '160px' }}>خط السير ومحطة الركوب</th>
                <th style={{ minWidth: '170px' }}>الاشتراك والرحلات</th>
                <th style={{ minWidth: '120px' }}>عنوان السكن</th>
                <th style={{ minWidth: '90px' }}>الحالة</th>
                <th style={{ minWidth: '280px', textAlign: 'center', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 2, boxShadow: '2px 0 6px rgba(0,0,0,0.04)' }}>إجراءات الإدارة</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStudents.map((s) => (
                <tr key={s.uid}>
                  {/* Name & Classification */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '50%',
                          backgroundColor: '#FFF1F2',
                          color: '#E5232A',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          flexShrink: 0,
                          border: '1px solid #FECDD3',
                        }}
                      >
                        {s.fullName?.charAt(0) || 'ط'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <strong
                          style={{
                            display: 'block',
                            color: '#0f172a',
                            fontWeight: 'bold',
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {s.fullName}
                        </strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                          <span
                            style={{
                              fontSize: '10px',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              padding: '1px 5px',
                              borderRadius: '4px',
                              fontFamily: 'monospace',
                            }}
                          >
                            {s.uid.substring(0, 8)}
                          </span>
                          {s.college && (
                            <span
                              style={{
                                fontSize: '10px',
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                padding: '1px 5px',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                              }}
                            >
                              {s.college}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Account Creation Date & Hour */}
                  <td>
                    {(() => {
                      const { date, time } = formatCreationDateTime(s.createdAt);
                      if (date === '—') {
                        return <span style={{ fontSize: '11px', color: '#94a3b8' }}>—</span>;
                      }
                      return (
                        <div
                          style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            gap: '3px',
                            background: '#F8FAFC',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#1E293B',
                              fontWeight: 700,
                              fontSize: '11.5px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Calendar size={12} style={{ color: '#0284C7' }} />
                            <span>{date}</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#64748B',
                              fontSize: '11px',
                              direction: 'rtl',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Clock size={11} style={{ color: '#D97706' }} />
                            <span>{time}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Phone Number with Call/Whatsapp */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }} dir="ltr">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Phone size={13} className="text-[#E5232A] flex-shrink-0" />
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold', color: '#0f172a' }}>
                          {s.phoneNumber || '—'}
                        </span>
                      </div>
                      {s.phoneNumber && (
                        <button
                          type="button"
                          onClick={() => handleSendWhatsAppWelcome(s)}
                          title="إرسال رسالة ترحيبية للطالب عبر الواتساب"
                          style={{
                            padding: '2px 6px',
                            background: '#ECFDF5',
                            color: '#059669',
                            borderRadius: '6px',
                            border: '1px solid #A7F3D0',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            fontSize: '11px',
                          }}
                        >
                          <WhatsAppIcon size={12} />
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Email */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} dir="ltr">
                      <Mail size={13} className="text-slate-400 flex-shrink-0" />
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          color: '#334155',
                          backgroundColor: '#f8fafc',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                          maxWidth: '170px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                        }}
                        title={s.email}
                      >
                        {s.email}
                      </span>
                    </div>
                  </td>

                  {/* University & Governorate */}
                  <td>
                    <div>
                      <strong style={{ display: 'block', fontSize: '12px', color: '#0f172a' }}>
                        {getStandardUniversityName(s.universityName || s.universityId)}
                      </strong>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                        <span
                          style={{
                            fontSize: '10px',
                            backgroundColor: '#fffbeb',
                            color: '#92400e',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            border: '1px solid #fde68a',
                          }}
                        >
                          {s.universityGovernorate || 'مصر'}
                        </span>
                        {s.academicYear && (
                          <span style={{ fontSize: '10px', color: '#64748b' }}>
                            ({s.academicYear})
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Assigned Route & Pickup Station */}
                  <td>
                    {s.assignedRoute ? (
                      <div>
                        <span
                          style={{
                            fontSize: '11px',
                            backgroundColor: '#FFF1F2',
                            color: '#E5232A',
                            fontWeight: 'bold',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            border: '1px solid #FECDD3',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            maxWidth: '180px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={s.assignedRoute}
                        >
                          <Bus size={11} className="flex-shrink-0" />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.assignedRoute}</span>
                        </span>
                        {s.defaultPickupStation && (
                          <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <MapPin size={10} className="text-slate-400" />
                            <span>{s.defaultPickupStation}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span
                        style={{
                          fontSize: '11px',
                          backgroundColor: '#fef3c7',
                          color: '#b45309',
                          fontWeight: 'bold',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          border: '1px solid #fde68a',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <AlertTriangle size={11} />
                        <span>غير معين</span>
                      </span>
                    )}
                  </td>

                  {/* Trip Stats & Multi-Trip Subscription */}
                  <td>
                    {(() => {
                      const tStats = studentTripStatsMap.get(s.uid);
                      if (!tStats || tStats.totalBooked === 0) {
                        return (
                          <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                            لا توجد رحلات
                          </span>
                        );
                      }
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {tStats.isMultiTripSubscriber ? (
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  backgroundColor: '#eff6ff',
                                  color: '#1d4ed8',
                                  padding: '1px 6px',
                                  borderRadius: '999px',
                                  border: '1px solid #bfdbfe',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                }}
                                title="مشترك في عدة رحلات"
                              >
                                <Ticket size={10} />
                                <span>عدة رحلات ({tStats.totalBooked})</span>
                              </span>
                            ) : (
                              <span
                                style={{
                                  fontSize: '10px',
                                  backgroundColor: '#f1f5f9',
                                  color: '#475569',
                                  padding: '1px 6px',
                                  borderRadius: '999px',
                                }}
                              >
                                حجز فردي
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'monospace' }}>
                            <span style={{ color: '#0f172a' }}>
                              محجوز: <strong style={{ color: '#E5232A' }}>{tStats.totalBooked}</strong>
                            </span>
                            <span style={{ color: '#cbd5e1' }}>|</span>
                            <span style={{ color: tStats.remaining > 0 ? '#16a34a' : '#64748b' }}>
                              متبقي: <strong>{tStats.remaining}</strong>
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Address */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#475569', fontSize: '12px' }}>
                      <MapPin size={12} className="text-slate-400 flex-shrink-0" />
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '120px',
                          display: 'inline-block',
                        }}
                        title={s.address}
                      >
                        {s.address || 'الإسكندرية'}
                      </span>
                    </div>
                  </td>

                  {/* Status Pill */}
                  <td>
                    <span className={`status-pill ${s.status === 'ACTIVE' ? 'active' : 'suspended'}`}>
                      {s.status === 'ACTIVE' ? 'نشط' : 'معلق'}
                    </span>
                  </td>

                  {/* Actions Column (Sticky Left) */}
                  <td style={{ position: 'sticky', left: 0, backgroundColor: '#ffffff', zIndex: 1, boxShadow: '2px 0 6px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                      {/* WhatsApp Welcome Message */}
                      <button
                        type="button"
                        className="btn-admin-outline"
                        style={{
                          padding: '4px 8px',
                          background: '#ECFDF5',
                          color: '#059669',
                          borderColor: '#A7F3D0',
                          fontSize: '11px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          fontWeight: 'bold',
                        }}
                        title="إرسال رسالة ترحيبية للطالب عبر الواتساب (بانضمامه لاوتو استيودنتس - شركة أوتوبيسي)"
                        onClick={() => handleSendWhatsAppWelcome(s)}
                      >
                        <WhatsAppIcon size={12} />
                        <span>واتساب</span>
                      </button>

                      {/* Edit Student Button */}
                      <button
                        type="button"
                        className="btn-admin-outline"
                        style={{
                          padding: '4px 8px',
                          background: '#F0FDF4',
                          color: '#16A34A',
                          borderColor: '#BBF7D0',
                          fontSize: '11px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                        title="تعديل بيانات الطالب"
                        onClick={() => handleOpenEditStudent(s)}
                      >
                        <Edit size={12} />
                        <span>تعديل</span>
                      </button>

                      {/* View Profile */}
                      <button
                        type="button"
                        className="btn-admin-outline"
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                        title="عرض الملف الكامل"
                        onClick={() => setSelectedStudent(s)}
                      >
                        <Eye size={12} />
                        <span>الملف</span>
                      </button>

                      {/* Direct Booking */}
                      <button
                        type="button"
                        className="btn-admin-outline"
                        style={{
                          padding: '4px 8px',
                          background: '#FFF1F2',
                          color: '#E5232A',
                          borderColor: '#FECDD3',
                          fontSize: '11px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                        title="حجز رحلة جديدة للطالب فوراً"
                        onClick={() => {
                          sessionStorage.setItem('nashgo_booking_target_student', JSON.stringify({ uid: s.uid }));
                          navigate('/admin/bookings');
                        }}
                      >
                        <Bus size={12} />
                        <span>حجز</span>
                      </button>

                      {/* Change Password */}
                      <button
                        type="button"
                        className="btn-admin-outline"
                        style={{
                          padding: '4px 7px',
                          background: '#FEF3C7',
                          color: '#B45309',
                          borderColor: '#FCD34D',
                          fontSize: '11px',
                        }}
                        title="تغيير كلمة المرور"
                        onClick={() => handleOpenChangePassword(s)}
                      >
                        <KeyRound size={12} />
                      </button>

                      {/* Suspend / Reactivate */}
                      {s.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          className="btn-admin-danger"
                          style={{ padding: '4px 7px', fontSize: '11px' }}
                          title="تعليق حساب الطالب"
                          onClick={() => handleToggleStatus(s)}
                          disabled={actionLoading}
                        >
                          <ShieldAlert size={12} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-admin-success"
                          style={{ padding: '4px 7px', fontSize: '11px' }}
                          title="إعادة تفعيل الحساب"
                          onClick={() => handleToggleStatus(s)}
                          disabled={actionLoading}
                        >
                          <ShieldCheck size={12} />
                        </button>
                      )}

                      {/* Delete Student (Super Admin Only) */}
                      {!isOperator && (
                        <button
                          type="button"
                          className="btn-admin-danger"
                          style={{
                            padding: '4px 7px',
                            background: '#FEE2E2',
                            color: '#DC2626',
                            borderColor: '#FCA5A5',
                            fontSize: '11px',
                          }}
                          title="حذف حساب الطالب نهائياً من قاعدة البيانات"
                          onClick={() => handleDeleteStudent(s)}
                          disabled={actionLoading}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Load More Footer (15 initial + 10 more on button click) */}
        {filteredStudents.length > 0 && (
          <div style={{ padding: '0 12px 14px' }}>
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredStudents.length)}
              totalCount={filteredStudents.length}
              itemName="طالب"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredStudents.length)}
            />
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {passwordModalStudent && (
        <div className="admin-modal-overlay" onClick={() => setPasswordModalStudent(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <form onSubmit={handleSaveNewPassword}>
              <div className="admin-modal-header">
                <div className="flex items-center gap-2">
                  <KeyRound size={20} className="text-[#E5232A]" />
                  <div>
                    <h3 className="text-base font-bold text-slate-800">
                      تغيير كلمة المرور: {passwordModalStudent.fullName}
                    </h3>
                    <span className="text-xs text-slate-500 font-mono" dir="ltr">
                      {passwordModalStudent.email}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setPasswordModalStudent(null)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 mb-4 text-xs text-amber-900 flex items-start gap-2">
                  <Sparkles size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span>
                      سيتم تحديث كلمة مرور الطالب فوراً في قاعدة البيانات حتى يتمكن من الدخول بها.
                    </span>
                  </div>
                </div>

                <div className="admin-form-group mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-800">كلمة المرور الجديدة *</label>
                    <button
                      type="button"
                      className="text-xs text-[#E5232A] font-bold"
                      onClick={() => {
                        const num = Math.floor(100000 + Math.random() * 900000);
                        setNewPasswordValue(`Oto${num}!`);
                      }}
                    >
                      توليد كلمة سر جديدة
                    </button>
                  </div>
                  <input
                    type="text"
                    required
                    dir="ltr"
                    className="font-mono font-bold text-sm"
                    value={newPasswordValue}
                    onChange={(e) => setNewPasswordValue(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex-1 py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    onClick={handleCopyPassword}
                  >
                    <Copy size={13} />
                    <span>{copiedPass ? 'تم نسخ الباسورد ✓' : 'نسخ الباسورد للحافظة'}</span>
                  </button>

                  {passwordModalStudent.phoneNumber && (
                    <button
                      type="button"
                      className="py-2 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all"
                      onClick={() => {
                        let phone = passwordModalStudent.phoneNumber?.replace(/[^0-9]/g, '') || '';
                        if (phone.startsWith('0')) phone = '2' + phone;
                        const msg = `مرحباً يا ${passwordModalStudent.fullName}، تم تحديث كلمة مرور حسابك في OTO STUDENTS إلى:\n🔑 ${newPasswordValue}\n\nرابط تسجيل الدخول:\nhttps://otostudents.com/login`;
                        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                    >
                      <Phone size={13} />
                      <span>واتساب</span>
                    </button>
                  )}

                  <button
                    type="button"
                    className="py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all"
                    onClick={handleSendResetEmail}
                    disabled={actionLoading}
                    title="إرسال رابط استعادة رسمي للبريد الإلكتروني"
                  >
                    <Mail size={13} />
                    <span>رابط للبريد</span>
                  </button>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setPasswordModalStudent(null)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={actionLoading || !newPasswordValue.trim()}
                >
                  <CheckCircle2 size={16} />
                  <span>حفظ كلمة المرور الجديدة</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student Profile Modal */}
      {selectedStudent && (
        <div className="admin-modal-overlay" onClick={() => setSelectedStudent(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>ملف الطالب: {selectedStudent.fullName}</h3>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setSelectedStudent(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="bg-slate-50 p-4 rounded-xl border flex items-center justify-between mb-4">
                <div>
                  <span className="text-xs text-slate-500 block">معرّف الطالب (UID):</span>
                  <strong className="font-mono text-xs text-[#E5232A]">{selectedStudent.uid}</strong>
                </div>
                <div className="text-left">
                  <span className="text-xs text-slate-500 block">حالة الحساب:</span>
                  <span className={`status-pill ${selectedStudent.status === 'ACTIVE' ? 'active' : 'suspended'}`}>
                    {selectedStudent.status === 'ACTIVE' ? 'نشط (ACTIVE)' : 'معلق (SUSPENDED)'}
                  </span>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="admin-form-group">
                  <label>الاسم بالكامل:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800 font-bold">
                    {selectedStudent.fullName}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>رقم الهاتف الأساسي:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg font-mono text-slate-800" dir="ltr">
                    {selectedStudent.phoneNumber || 'غير مسجل'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>البريد الإلكتروني:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg font-mono text-slate-800" dir="ltr">
                    {selectedStudent.email}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>الجامعة المسجل بها:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800 font-bold">
                    {selectedStudent.universityName || selectedStudent.universityId}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>خط السير المعين:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-[#E5232A] font-bold">
                    {selectedStudent.assignedRoute || 'غير محدد بعد'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>عنوان السكن بالإسكندرية:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800">
                    {selectedStudent.address || 'غير محدد'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>كلمة المرور المسجلة:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg font-mono text-slate-800 font-bold" dir="ltr">
                    {selectedStudent.password || '— (مشفرة في Auth)'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>تاريخ ووقت إنشاء الحساب:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800">
                    {(() => {
                      const { date, time } = formatCreationDateTime(selectedStudent.createdAt);
                      if (date === '—') return <span className="font-mono text-slate-400">—</span>;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 700, fontSize: '12px', color: '#0f172a' }}>
                            <Calendar size={14} style={{ color: '#0284c7' }} />
                            <span>{date}</span>
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: '#475569' }} dir="rtl">
                            <Clock size={13} style={{ color: '#d97706' }} />
                            <span>{time}</span>
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Trip Subscription Stats Box (بيانات الاشتراك والرحلات المحجوزة والمتبقية) */}
              {(() => {
                const tStats = studentTripStatsMap.get(selectedStudent.uid);
                return (
                  <div style={{ backgroundColor: '#eff6ff', padding: '14px 16px', borderRadius: '12px', border: '1px solid #bfdbfe', marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Ticket size={18} style={{ color: '#2563eb' }} />
                        <h4 style={{ fontWeight: 'bold', fontSize: '14px', color: '#1e3a8a', margin: 0 }}>
                          بيانات الاشتراك وحالة الرحلات المحجوزة
                        </h4>
                      </div>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '3px 10px',
                          borderRadius: '999px',
                          backgroundColor: tStats?.isMultiTripSubscriber ? '#2563eb' : (tStats?.totalBooked ? '#dbeafe' : '#e2e8f0'),
                          color: tStats?.isMultiTripSubscriber ? '#ffffff' : (tStats?.totalBooked ? '#1e40af' : '#475569'),
                        }}
                      >
                        {tStats?.isMultiTripSubscriber ? 'مشترك في عدة رحلات (اشتراك متعدد)' : (tStats?.totalBooked ? 'حجز فردي' : 'لا توجد حجوزات')}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center' }}>
                      <div style={{ backgroundColor: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>إجمالي المحجوز</span>
                        <strong style={{ fontSize: '18px', fontFamily: 'monospace', color: '#2563eb' }}>{tStats?.totalBooked || 0}</strong>
                        <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>رحلة</span>
                      </div>
                      <div style={{ backgroundColor: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>الرحلات المتبقية</span>
                        <strong style={{ fontSize: '18px', fontFamily: 'monospace', color: '#16a34a' }}>{tStats?.remaining || 0}</strong>
                        <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>رحلة قادمة</span>
                      </div>
                      <div style={{ backgroundColor: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>الرحلات المكتملة</span>
                        <strong style={{ fontSize: '18px', fontFamily: 'monospace', color: '#64748b' }}>{tStats?.completed || 0}</strong>
                        <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>رحلة سابقة</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="btn-admin-outline"
                onClick={() => setSelectedStudent(null)}
              >
                إغلاق النافذة
              </button>

              <button
                type="button"
                className="btn-admin-primary"
                onClick={() => {
                  const s = selectedStudent;
                  setSelectedStudent(null);
                  handleOpenEditStudent(s);
                }}
              >
                <Edit size={15} />
                <span>تعديل بيانات الطالب</span>
              </button>

              <button
                type="button"
                className="btn-admin-success flex items-center gap-1.5"
                style={{
                  backgroundColor: '#059669',
                  color: '#ffffff',
                  borderColor: '#059669',
                  padding: '7px 12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
                onClick={() => handleSendWhatsAppWelcome(selectedStudent)}
                title="إرسال رسالة ترحيبية للطالب عبر الواتساب (بانضمامه لاوتو استيودنتس - شركة أوتوبيسي)"
              >
                <WhatsAppIcon size={15} />
                <span>ترحيب واتساب</span>
              </button>

              <button
                type="button"
                className="btn-admin-outline"
                style={{ background: '#FEF3C7', color: '#B45309', borderColor: '#FCD34D' }}
                onClick={() => {
                  setSelectedStudent(null);
                  handleOpenChangePassword(selectedStudent);
                }}
              >
                <KeyRound size={15} />
                <span>تغيير كلمة المرور</span>
              </button>

              {!isOperator && (
                <button
                  type="button"
                  className="btn-admin-danger"
                  onClick={() => {
                    const s = selectedStudent;
                    setSelectedStudent(null);
                    handleDeleteStudent(s);
                  }}
                >
                  <Trash2 size={15} />
                  <span>حذف حساب الطالب</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {editingStudent && (
        <div className="admin-modal-overlay" onClick={() => setEditingStudent(null)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '640px', width: '95%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSaveStudentEdit}>
              <div className="admin-modal-header">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Edit size={18} className="text-[#E5232A]" />
                  <span>تعديل بيانات الطالب: {editingStudent.fullName}</span>
                </h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setEditingStudent(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="form-grid-2">
                  {/* Full Name */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">الاسم بالكامل *</label>
                    <input
                      type="text"
                      required
                      className="admin-search-input w-full"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.fullName}
                      onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">رقم الهاتف (واتساب) *</label>
                    <input
                      type="tel"
                      required
                      dir="ltr"
                      className="admin-search-input w-full font-mono"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.phoneNumber}
                      onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                    />
                  </div>

                  {/* Email */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">البريد الإلكتروني *</label>
                    <input
                      type="email"
                      required
                      dir="ltr"
                      className="admin-search-input w-full font-mono"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </div>

                  {/* University */}
                  <div className="admin-form-group">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-bold text-slate-800">الجامعة المسجل بها</label>
                      {editForm.isCustomUniversity && (
                        <span className="text-[11px] font-bold text-[#E5232A] bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                          إدخال يدوي
                        </span>
                      )}
                    </div>
                    <select
                      className="admin-filter-select w-full"
                      value={editForm.isCustomUniversity ? 'OTHER' : editForm.universityId}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'OTHER') {
                          setEditForm({
                            ...editForm,
                            universityId: 'OTHER',
                            isCustomUniversity: true,
                          });
                        } else {
                          const found = allUniversityOptions.find((u) => u.id === val);
                          setEditForm({
                            ...editForm,
                            universityId: val,
                            universityName: found ? found.name : '',
                            universityGovernorate: found ? found.governorate : editForm.universityGovernorate,
                            isCustomUniversity: false,
                          });
                        }
                      }}
                    >
                      <option value="">-- اختر الجامعة من القائمة المسجلة --</option>
                      {allUniversityOptions.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.governorate})
                        </option>
                      ))}
                      <option value="OTHER" style={{ fontWeight: 'bold', color: '#E5232A' }}>
                        ✏️ أخرى (إدخال اسم الجامعة يدوياً...)
                      </option>
                    </select>

                    {/* Manual University Input - Shows when OTHER is selected or isCustomUniversity */}
                    {editForm.isCustomUniversity && (
                      <div className="mt-2.5 p-3 bg-rose-50/70 rounded-xl border border-rose-200 space-y-2">
                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            اسم الجامعة (إدخال يدوي) *
                          </label>
                          <input
                            type="text"
                            required={editForm.isCustomUniversity}
                            placeholder="اكتب اسم الجامعة يدوياً (مثال: جامعة العلمين، جامعة بدر...)"
                            className="admin-search-input w-full bg-white text-xs"
                            style={{ paddingRight: '1rem', borderColor: '#FCA5A5' }}
                            value={editForm.universityName}
                            onChange={(e) =>
                              setEditForm({ ...editForm, universityName: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                            المحافظة / المدينة (اختياري)
                          </label>
                          <input
                            type="text"
                            placeholder="مثال: الإسكندرية، مطروح، الجيزة، المنوفية..."
                            className="admin-search-input w-full bg-white text-xs"
                            style={{ paddingRight: '1rem' }}
                            value={editForm.universityGovernorate}
                            onChange={(e) =>
                              setEditForm({ ...editForm, universityGovernorate: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* College / Faculty */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">الكلية / التخصص</label>
                    <input
                      type="text"
                      placeholder="مثال: هندسة، طب، تجارة..."
                      className="admin-search-input w-full"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.college}
                      onChange={(e) => setEditForm({ ...editForm, college: e.target.value })}
                    />
                  </div>

                  {/* Academic Year */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">السنة الدراسية</label>
                    <input
                      type="text"
                      placeholder="مثال: الفرقة الأولى / الفرقة الثالثة..."
                      className="admin-search-input w-full"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.academicYear}
                      onChange={(e) => setEditForm({ ...editForm, academicYear: e.target.value })}
                    />
                  </div>

                  {/* Assigned Route */}
                  <div className="admin-form-group">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-bold text-slate-800">خط السير المعين</label>
                      <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold">
                        ⚡ مزامنة تلقائية مع الحجوزات والطلبات
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="مثال: الموقف الجديد بمحرم بيك ◄ جامعة طنطا"
                      className="admin-search-input w-full font-bold text-xs"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.assignedRoute}
                      onChange={(e) => setEditForm({ ...editForm, assignedRoute: e.target.value })}
                    />
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          const targetUni = editForm.universityName || 'الجامعة';
                          setEditForm({
                            ...editForm,
                            assignedRoute: `الموقف الجديد بمحرم بيك ◄ ${targetUni}`,
                            defaultPickupStation: 'الموقف الجديد بمحرم بيك',
                          });
                        }}
                        className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded border border-slate-200 transition-colors font-medium flex items-center gap-1"
                      >
                        <Bus size={10} />
                        الموقف الجديد بمحرم بيك ◄ {editForm.universityName || 'الجامعة'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const targetUni = editForm.universityName || 'الجامعة';
                          setEditForm({
                            ...editForm,
                            assignedRoute: `الكيلو 21 ◄ ${targetUni}`,
                            defaultPickupStation: 'الكيلو 21',
                          });
                        }}
                        className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded border border-slate-200 transition-colors font-medium flex items-center gap-1"
                      >
                        <Bus size={10} />
                        الكيلو 21 ◄ {editForm.universityName || 'الجامعة'}
                      </button>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">عنوان السكن بالإسكندرية</label>
                    <input
                      type="text"
                      placeholder="مثال: سموحة - بجوار شارع فوزي معاذ"
                      className="admin-search-input w-full"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    />
                  </div>

                  {/* Preferred Pickup Station */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">نقطة الانطلاق / محطة الركوب المعتمدة</label>
                    <div className="flex gap-2 mb-1.5">
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, defaultPickupStation: 'الموقف الجديد بمحرم بيك' })}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border font-bold flex-1 transition-colors ${
                          editForm.defaultPickupStation === 'الموقف الجديد بمحرم بيك'
                            ? 'bg-[#FFF1F2] border-[#E5232A] text-[#E5232A]'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        الموقف الجديد بمحرم بيك
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, defaultPickupStation: 'الكيلو 21' })}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border font-bold flex-1 transition-colors ${
                          editForm.defaultPickupStation === 'الكيلو 21'
                            ? 'bg-[#FFF1F2] border-[#E5232A] text-[#E5232A]'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        الكيلو 21
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="أو محطة مخصصة يتم تحديدها وتعديلها من قبل الإدارة..."
                      className="admin-search-input w-full text-xs"
                      style={{ paddingRight: '1rem' }}
                      value={editForm.defaultPickupStation}
                      onChange={(e) => setEditForm({ ...editForm, defaultPickupStation: e.target.value })}
                    />
                    <span className="text-[10px] text-slate-400 mt-1 block">
                      * تقتصر محطات الانطلاق قياسياً على الموقف الجديد أو الكيلو 21 ما لم تعدلها الإدارة.
                    </span>
                  </div>

                  {/* Status */}
                  <div className="admin-form-group">
                    <label className="text-xs font-bold text-slate-800">حالة الحساب</label>
                    <select
                      className="admin-filter-select w-full"
                      value={editForm.status}
                      onChange={(e) =>
                        setEditForm({ ...editForm, status: e.target.value as 'ACTIVE' | 'SUSPENDED' })
                      }
                    >
                      <option value="ACTIVE">نشط (ACTIVE) — مسموح له بالحجز</option>
                      <option value="SUSPENDED">معلق (SUSPENDED) — ممنوع من الحجز</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div className="admin-form-group mt-3">
                  <label className="text-xs font-bold text-slate-800">ملاحظات الإدارة عن الطالب</label>
                  <textarea
                    rows={2}
                    className="admin-search-input w-full"
                    style={{ padding: '0.65rem 1rem', height: 'auto', resize: 'vertical' }}
                    placeholder="ملاحظات خاصة، تعليمات الاتصال، أو اشتراك الطالب..."
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setEditingStudent(null)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary flex items-center gap-1.5"
                  disabled={actionLoading}
                >
                  <CheckCircle2 size={16} />
                  <span>{actionLoading ? 'جاري الحفظ...' : 'حفظ التعديلات'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminStudents;
